import { CheckCircle, ChevronRight, Close, ExpandLess, ExpandMore, HealthAndSafety,
	PhotoCamera, RemoveCircle, ReportProblem, Shield, Warning } from "@mui/icons-material";
import { Button, MenuItem, Radio, TextField, Typography } from "@mui/material";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../../components/contextProviders/AlertProvider";
import EpiCancelInspectionModal from "../../../components/epi/EpiCancelInspectionModal";
import EpiWizardActions from "../../../components/epi/EpiWizardActions";
import { EPI_COLORS, reasonLabels } from "../../../components/epi/epiColors";
import { EpiInspectionFlowService } from "../../../services/EpiInspectionFlowService";
import { EpiInspectionItemDTO } from "../../../types/EpiInspectionFlowDTO";
import { StepProps } from "../EpiInspectionWizard";

export const IMMEDIATE_ACTIONS: Record<string, string> = {
	ORIENTACAO_NO_MOMENTO: 'Orientação realizada no momento',
	SUBSTITUICAO_IMEDIATA: 'Substituição imediata',
	RETIRADA_DE_USO: 'Retirada de uso',
	ENCAMINHADO_AO_SESMT: 'Encaminhado ao SESMT'
};

const OTHER_ACTION = 'OUTRA';

const NR06_NOTES = [
	{ icon: <Shield fontSize="small" />, color: EPI_COLORS.info,
		text: 'A organização deve fornecer EPI adequado, gratuitamente e em perfeito estado.' },
	{ icon: <HealthAndSafety fontSize="small" />, color: EPI_COLORS.purple,
		text: 'O colaborador deve utilizar corretamente e zelar pela conservação do EPI.' },
	{ icon: <Warning fontSize="small" />, color: EPI_COLORS.warning,
		text: 'Irregularidades devem ser corrigidas imediatamente e registradas.' }
];

const statusOptions = [
	{ value: 'OK', label: 'OK', color: EPI_COLORS.ok },
	{ value: 'NAO_OK', label: 'NÃO OK', color: EPI_COLORS.danger },
	{ value: 'NAO_APLICAVEL', label: 'NÃO APLICÁVEL', color: EPI_COLORS.neutral }
];

const EpiChecklistStep: FC<StepProps> = ({ inspection, onSave }) => {
	const [items, setItems] = useState<EpiInspectionItemDTO[]>(inspection.items);
	const [observation, setObservation] = useState(inspection.generalObservation || '');
	const [expanded, setExpanded] = useState<number | null>(null);
	const [cancelOpen, setCancelOpen] = useState(false);
	const [customActionRows, setCustomActionRows] = useState<number[]>([]);
	const navigate = useNavigate();
	const allClassified = items.every(item => !!item.status);
	const missingReason = items.some(item => item.status === 'NAO_OK' && !item.nonConformityReason);
	const missingReasonDetail = items.some(item => item.status === 'NAO_OK'
		&& item.nonConformityReason === 'OUTRO' && !item.nonConformityReasonDetail?.trim());
	const missingActionDetail = items.some((item, index) => item.status === 'NAO_OK'
		&& isCustomAction(item, index) && !item.immediateAction?.trim());
	const okCount = items.filter(item => item.status === 'OK').length;
	const notOkCount = items.filter(item => item.status === 'NAO_OK').length;
	const notApplicableCount = items.filter(item => item.status === 'NAO_APLICAVEL').length;
	const service = new EpiInspectionFlowService();
	const { showError } = useAlert();

	function update(index: number, changes: Partial<EpiInspectionItemDTO>) {
		setItems(previous => previous.map((item, position) =>
			position === index ? { ...item, ...changes } : item));
		if (changes.status === 'NAO_OK') {
			setExpanded(index);
		}
	}

	function isCustomAction(item: EpiInspectionItemDTO, index: number) {
		return customActionRows.includes(index)
			|| (!!item.immediateAction && !IMMEDIATE_ACTIONS[item.immediateAction]);
	}

	function changeReason(index: number, reason: string) {
		update(index, {
			nonConformityReason: reason,
			nonConformityReasonDetail: reason === 'OUTRO' ? items[index].nonConformityReasonDetail : undefined
		});
	}

	function changeAction(index: number, value: string) {
		if (value === OTHER_ACTION) {
			setCustomActionRows(previous => previous.includes(index) ? previous : [...previous, index]);
			if (IMMEDIATE_ACTIONS[items[index].immediateAction || '']) {
				update(index, { immediateAction: '' });
			}
			return;
		}
		setCustomActionRows(previous => previous.filter(row => row !== index));
		update(index, { immediateAction: value });
	}

	return (
		<div className="passo">
			<div className="principal">
				<div className="painel">
					<div className="titulo-painel">
						<Typography variant="subtitle1">Equipamentos de Proteção Individual</Typography>
						<span>Selecione o status de cada EPI inspecionado.</span>
					</div>
					<div className="tabela tabela-checklist">
						<div className="cabecalho-tabela">
							<span>EPI RECOMENDADO PARA A FUNÇÃO</span>
							<span style={{ color: EPI_COLORS.ok }}>OK</span>
							<span style={{ color: EPI_COLORS.danger }}>NÃO OK</span>
							<span>NÃO APLICÁVEL</span>
							<span>CA</span>
							<span>ÚLTIMA ENTREGA</span>
							<span />
						</div>
						{items.map((item, index) => (
							<div className="grupo-linha" key={item.id || index}>
								<div className="linha">
									<div className="epi-nome">
										<span className="icone-epi" style={{ backgroundColor: epiColorOf(index) }}>
											<HealthAndSafety fontSize="small" />
										</span>
										<span>{item.epiName}</span>
									</div>
									{statusOptions.map(option => (
										<div className="opcao-status" key={option.value}>
											<Radio
												size="small"
												name={`status-epi-${item.id || index}`}
												checked={item.status === option.value}
												style={{ color: option.color }}
												onChange={() => update(index, { status: option.value })}
											/>
											<span className="rotulo-status" style={{ color: option.color }}>
												{option.label}
											</span>
										</div>
									))}
									<span data-label="CA">{item.ca || '—'}</span>
									<span data-label="ÚLTIMA ENTREGA">{item.lastDeliveryDate || '—'}</span>
									{item.status === 'NAO_OK' && (
										<Button
											size="small"
											onClick={() => setExpanded(previous => previous === index ? null : index)}
										>
											{expanded === index ? <ExpandLess /> : <ExpandMore />}
										</Button>
									)}
								</div>
								{expanded === index && item.status === 'NAO_OK' && (
									<div className="nao-conformidade">
										<div className="campo-com-detalhe">
											<TextField
												select
												required
												label="Motivo da não conformidade"
												size="small"
												value={item.nonConformityReason || ''}
												onChange={(e) => changeReason(index, e.target.value)}
											>
												{Object.keys(reasonLabels).map(key => (
													<MenuItem key={key} value={key}>
														{key === 'OUTRO' ? 'Outro motivo (descrever)' : reasonLabels[key]}
													</MenuItem>
												))}
											</TextField>
											{item.nonConformityReason === 'OUTRO' && (
												<TextField
													required
													autoFocus
													label="Descreva o motivo"
													size="small"
													multiline
													minRows={2}
													value={item.nonConformityReasonDetail || ''}
													onChange={(e) => update(index, { nonConformityReasonDetail: e.target.value })}
												/>
											)}
										</div>
										<div className="campo-com-detalhe">
											<TextField
												select
												label="Ação imediata realizada"
												size="small"
												value={isCustomAction(item, index) ? OTHER_ACTION : item.immediateAction || ''}
												onChange={(e) => changeAction(index, e.target.value)}
											>
												{Object.keys(IMMEDIATE_ACTIONS).map(key => (
													<MenuItem key={key} value={key}>{IMMEDIATE_ACTIONS[key]}</MenuItem>
												))}
												<MenuItem value={OTHER_ACTION}>Outra ação (descrever)</MenuItem>
											</TextField>
											{isCustomAction(item, index) && (
												<TextField
													required
													autoFocus
													label="Descreva a ação"
													size="small"
													multiline
													minRows={2}
													value={item.immediateAction || ''}
													onChange={(e) => update(index, { immediateAction: e.target.value })}
												/>
											)}
										</div>
										<TextField
											label="Observação específica"
											size="small"
											multiline
											minRows={2}
											value={item.observation || ''}
											onChange={(e) => update(index, { observation: e.target.value })}
										/>
										<div className="evidencia">
											<Button variant="outlined" component="label" startIcon={<PhotoCamera />}>
												Anexar foto
												<input
													hidden
													type="file"
													accept="image/*"
													capture="environment"
													onChange={(e) => attach(item, e.target.files)}
												/>
											</Button>
											{!!item.evidenceFiles && <span>Evidência anexada.</span>}
										</div>
									</div>
								)}
							</div>
						))}
					</div>
					<TextField
						label="Observação geral (opcional)"
						placeholder="Digite observações sobre a inspeção, se necessário..."
						size="small"
						multiline
						minRows={3}
						value={observation}
						onChange={(e) => setObservation(e.target.value)}
					/>
				</div>
				<EpiWizardActions
					leftLabel="CANCELAR INSPEÇÃO"
					leftIcon={<Close />}
					onLeft={() => setCancelOpen(true)}
					draftLabel="SALVAR RASCUNHO"
					onDraft={() => onSave(0, items, observation)}
					primaryLabel="CONTINUAR"
					primaryIcon={<ChevronRight />}
					primaryDisabled={!allClassified || missingReason || missingReasonDetail || missingActionDetail}
					onPrimary={() => onSave(1, items, observation)}
					hint={!allClassified
						? 'Classifique todos os EPIs para continuar.'
						: missingReason
							? 'Informe o motivo de cada EPI não conforme.'
							: missingReasonDetail
								? 'Descreva o motivo de cada EPI não conforme.'
								: missingActionDetail
									? 'Descreva a ação imediata de cada EPI não conforme.'
									: undefined}
				/>
			</div>
			<div className="lateral">
				<div className="painel">
					<Typography variant="subtitle1">Resumo da inspeção</Typography>
					<div className="contador" style={{ borderLeftColor: EPI_COLORS.ok }}>
						<CheckCircle style={{ color: EPI_COLORS.ok }} />
						<div>
							<Typography variant="h6" style={{ color: EPI_COLORS.ok }}>{okCount}</Typography>
							<span>OK</span>
						</div>
					</div>
					<div className="contador" style={{ borderLeftColor: EPI_COLORS.danger }}>
						<ReportProblem style={{ color: EPI_COLORS.danger }} />
						<div>
							<Typography variant="h6" style={{ color: EPI_COLORS.danger }}>{notOkCount}</Typography>
							<span>Não OK</span>
						</div>
					</div>
					<div className="contador" style={{ borderLeftColor: EPI_COLORS.neutral }}>
						<RemoveCircle style={{ color: EPI_COLORS.neutral }} />
						<div>
							<Typography variant="h6" style={{ color: EPI_COLORS.neutral }}>
								{notApplicableCount}
							</Typography>
							<span>Não aplicável</span>
						</div>
					</div>
					<div className="contador" style={{ borderLeftColor: EPI_COLORS.warning }}>
						<Warning style={{ color: EPI_COLORS.warning }} />
						<div>
							<Typography variant="h6" style={{ color: EPI_COLORS.warning }}>{notOkCount}</Typography>
							<span>Não conformidade</span>
						</div>
					</div>
				</div>
				<div className="painel">
					<Typography variant="subtitle1">Informações importantes</Typography>
					{NR06_NOTES.map(note => (
						<div className="nota" key={note.text}>
							<span className="icone-nota" style={{ backgroundColor: note.color }}>
								{note.icon}
							</span>
							<span>{note.text}</span>
						</div>
					))}
				</div>
			</div>
			<EpiCancelInspectionModal
				open={cancelOpen}
				onClose={() => setCancelOpen(false)}
				onConfirm={cancelInspection}
			/>
		</div>
	);

	function epiColorOf(index: number) {
		const palette = [EPI_COLORS.info, EPI_COLORS.purple, EPI_COLORS.warning, EPI_COLORS.ok,
			EPI_COLORS.orange, EPI_COLORS.neutral];
		return palette[index % palette.length];
	}

	async function attach(item: EpiInspectionItemDTO, files: FileList | null) {
		if (!files || !files.length || !item.id) {
			return;
		}
		const form = new FormData();
		form.append('files', files[0]);
		try {
			const response = await service.attachEvidence(inspection.id, item.id, form);
			const saved = response.data.items.find(current => current.id === item.id);
			setItems(previous => previous.map(current => current.id === item.id
				? { ...current, evidenceFiles: saved?.evidenceFiles }
				: current));
		} catch (error: any) {
			showError(error?.response?.data || 'Não foi possível anexar a evidência.');
		}
	}

	async function cancelInspection(reason: string) {
		try {
			const response = await service.cancel(inspection.id, reason);
			if (!response?.data) {
				showError('Não foi possível cancelar a inspeção.');
				return;
			}
			setCancelOpen(false);
			navigate('/inspecao-epis/nova');
		} catch (error: any) {
			showError(error?.response?.data || 'Não foi possível cancelar a inspeção.');
		}
	}
};

export default EpiChecklistStep;
